import React, { useState, useEffect } from 'react';
import { Download, X } from 'lucide-react';

export const InstallPopup: React.FC = () => {
  const [deferredPrompt, setDeferredPrompt] = useState<any>(null);
  const [showInstall, setShowInstall] = useState(false);

  useEffect(() => {
    // Check if the user has already declined/accepted installation
    const isDismissed = localStorage.getItem('installDismissed');
    
    // Check if app is already installed/standalone
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches;

    if (isDismissed || isStandalone) {
      return;
    }

    const handleInstallPromptEvent = () => {
      const prompt = (window as any).deferredPrompt;
      if (prompt) {
        setDeferredPrompt(prompt);
        setShowInstall(true);
      }
    };

    // Check if it already fired before this mounted
    handleInstallPromptEvent();

    const handleBeforeInstallPrompt = (e: Event) => {
      e.preventDefault();
      (window as any).deferredPrompt = e;
      handleInstallPromptEvent();
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
    window.addEventListener('app-install-prompt', handleInstallPromptEvent);

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstallPrompt);
      window.removeEventListener('app-install-prompt', handleInstallPromptEvent);
    };
  }, []);

  const handleInstallClick = async () => {
    if (!deferredPrompt) return;
    
    setShowInstall(false);
    
    // Show the install prompt
    deferredPrompt.prompt();
    
    // Wait for the user to respond to the prompt
    const { outcome } = await deferredPrompt.userChoice;
    
    if (outcome === 'accepted') {
      console.log('User accepted the install prompt');
    } else {
      console.log('User dismissed the install prompt');
    }
    
    setDeferredPrompt(null);
  };

  const handleDismiss = () => {
    setShowInstall(false);
    localStorage.setItem('installDismissed', 'true'); // don't show it again for logic
  };

  if (!showInstall) return null;

  return (
    <div className="fixed bottom-20 left-4 right-4 md:left-auto md:right-4 md:bottom-4 md:w-96 bg-white dark:bg-gray-800 rounded-xl shadow-xl border border-gray-200 dark:border-gray-700 p-4 z-50 flex items-start gap-4 animate-in slide-in-from-bottom-5">
      <div className="bg-red-50 dark:bg-red-900/20 p-2 rounded-lg text-[#D32F2F] shrink-0">
        <Download size={24} />
      </div>
      <div className="flex-1">
        <h3 className="font-semibold text-gray-900 dark:text-gray-100">ইনস্টল করুন</h3>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-1">অফলাইনে খবর পড়তে এবং ফাস্ট লোডিং পেতে অ্যাপটি ইনস্টল করুন।</p>
        <div className="flex items-center gap-3 mt-3">
          <button 
            onClick={handleInstallClick}
            className="flex-1 bg-[#D32F2F] hover:bg-[#D32F2F] text-white py-1.5 px-3 rounded-lg font-medium text-sm transition-colors"
          >
            ইনস্টল
          </button>
          <button 
            onClick={handleDismiss}
            className="flex-1 bg-gray-100 hover:bg-gray-200 dark:bg-gray-700 dark:hover:bg-gray-600 text-gray-700 dark:text-gray-200 py-1.5 px-3 rounded-lg font-medium text-sm transition-colors"
          >
            পরে
          </button>
        </div>
      </div>
      <button 
        onClick={handleDismiss}
        className="text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 absolute top-1 right-1 p-2 min-w-[44px] min-h-[44px] flex items-center justify-center rounded-full active:bg-gray-100 dark:active:bg-gray-700"
        aria-label="Dismiss"
      >
        <X size={16} />
      </button>
    </div>
  );
};
